import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { validationAPI, statsAPI } from '../services/api';

const TYPE_COLORS = {
    A: '#3b82f6',
    AAAA: '#8b5cf6',
    CNAME: '#f38020',
    MX: '#10b981',
    TXT: '#f59e0b',
    NS: '#6b7280',
    SRV: '#ec4899',
    CAA: '#14b8a6',
};

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [recordStats, setRecordStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingStats, setLoadingStats] = useState(true);
    const [validating, setValidating] = useState(false);

    const loadStats = async () => {
        try {
            setValidating(true);
            const [validationResponse, recordStatsResponse] = await Promise.all([
                validationAPI.validateAll(),
                statsAPI.getRecordStats(),
            ]);
            setStats(validationResponse.data);
            setRecordStats(recordStatsResponse.data);
        } catch (error) {
            console.error('Error loading stats:', error);
        } finally {
            setLoading(false);
            setLoadingStats(false);
            setValidating(false);
        }
    };

    useEffect(() => {
        loadStats();
    }, []);

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
                <span>Carregando estatísticas...</span>
            </div>
        );
    }

    // Convert typeDistribution object {A:5,CNAME:3} to array [{type:'A',count:5},...]
    const typeDistArray = recordStats?.typeDistribution
        ? Object.entries(recordStats.typeDistribution).map(([type, count]) => ({ type, count }))
        : [];

    const mostCommonType = typeDistArray.length > 0
        ? typeDistArray.reduce((max, item) => (item.count > (max?.count || 0) ? item : max), null)
        : null;

    const totalRecords = recordStats?.totalRecords || 0;

    // zonesWithCounts has {name, total, types} - convert to {domain, count}
    const top10Zones = recordStats?.zonesWithCounts
        ? recordStats.zonesWithCounts.slice(0, 10).map(z => ({ domain: z.name, count: z.total }))
        : [];

    const getRecordCountForDomain = (domain) => {
        if (!recordStats?.zonesWithCounts) return null;
        const zone = recordStats.zonesWithCounts.find(z => z.name === domain);
        return zone ? zone.total : 0;
    };

    return (
        <div>
            <div className="card-header">
                <h2 className="card-title">📊 Dashboard - Visão Geral</h2>
                <button
                    className="btn btn-primary"
                    onClick={loadStats}
                    disabled={validating}
                >
                    {validating ? '⏳ Validando...' : '🔄 Atualizar'}
                </button>
            </div>

            {stats && (
                <>
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-label">Total de Domínios</div>
                            <div className="stat-value">{stats.total}</div>
                        </div>

                        <div className="stat-card success">
                            <div className="stat-label">Configurados</div>
                            <div className="stat-value">{stats.configured}</div>
                        </div>

                        <div className="stat-card warning">
                            <div className="stat-label">Pendentes</div>
                            <div className="stat-value">{stats.pending}</div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-label">Taxa de Sucesso</div>
                            <div className="stat-value">
                                {((stats.configured / stats.total) * 100).toFixed(1)}%
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-label">Total DNS Records</div>
                            <div className="stat-value">
                                {loadingStats ? '...' : totalRecords}
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-label">Tipo Mais Comum</div>
                            <div className="stat-value">
                                {loadingStats ? '...' : mostCommonType ? `${mostCommonType.type} (${mostCommonType.count})` : '-'}
                            </div>
                        </div>
                    </div>

                    {typeDistArray.length > 0 && (
                        <div className="dashboard-charts-row">
                            <div className="chart-card">
                                <h3 className="card-title">Distribuicao por Tipo de Registro</h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={typeDistArray}
                                            dataKey="count"
                                            nameKey="type"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={100}
                                            label={({ type, count }) => `${type}: ${count}`}
                                        >
                                            {typeDistArray.map((entry, index) => (
                                                <Cell
                                                    key={`cell-${index}`}
                                                    fill={TYPE_COLORS[entry.type] || '#999999'}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="chart-card">
                                <h3 className="card-title">Top 10 Domínios por Registros</h3>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={top10Zones} layout="vertical" margin={{ left: 100 }}>
                                        <XAxis type="number" />
                                        <YAxis type="category" dataKey="domain" width={90} tick={{ fontSize: 12 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#3b82f6" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}

                    <div className="card">
                        <h3 className="card-title">Status dos Nameservers</h3>

                        <div className="table-container">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Domínio</th>
                                        <th>Status</th>
                                        <th>Configurado</th>
                                        <th>Records</th>
                                        <th>Nameservers Esperados</th>
                                        <th>Nameservers Atuais</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.results.map((result) => (
                                        <tr key={result.zone_id}>
                                            <td>
                                                <strong>{result.domain}</strong>
                                            </td>
                                            <td>
                                                <span className={`badge ${result.status === 'active' ? 'success' : 'pending'}`}>
                                                    {result.status}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`badge ${result.configured ? 'success' : 'error'}`}>
                                                    {result.configured ? '✓ Sim' : '✗ Não'}
                                                </span>
                                            </td>
                                            <td>
                                                {loadingStats ? '...' : (getRecordCountForDomain(result.domain) ?? '-')}
                                            </td>
                                            <td>
                                                <ul className="nameservers-list">
                                                    {result.expected.map((ns, idx) => (
                                                        <li key={idx}>{ns}</li>
                                                    ))}
                                                </ul>
                                            </td>
                                            <td>
                                                {result.actual.length > 0 ? (
                                                    <ul className="nameservers-list">
                                                        {result.actual.map((ns, idx) => (
                                                            <li key={idx} style={{
                                                                background: result.configured ? '#d1fae5' : '#fee2e2',
                                                                color: result.configured ? '#065f46' : '#991b1b'
                                                            }}>
                                                                {ns}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <span className="badge error">Nenhum encontrado</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {stats.pending > 0 && (
                        <div className="card" style={{ borderLeft: '4px solid #f59e0b', background: '#fffbeb' }}>
                            <h3 style={{ color: '#92400e', marginBottom: '1rem' }}>
                                ⚠️ Ação Necessária
                            </h3>
                            <p style={{ color: '#78350f', marginBottom: '1rem' }}>
                                Você tem <strong>{stats.pending} domínio(s)</strong> com nameservers pendentes de configuração.
                            </p>
                            <p style={{ color: '#78350f' }}>
                                Acesse seu registrador de domínios (Registro.br, etc.) e atualize os nameservers
                                para os valores mostrados na tabela acima.
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Dashboard;
